import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  fetchAuthSession,
  getCurrentUser,
  signOut as cognitoSignOut,
} from 'aws-amplify/auth';
import { authConfigured } from './authConfig';

type AuthState = {
  /** Username of the signed-in user, or null when signed out. */
  username: string | null;
  /** True until the initial session check finishes. */
  loading: boolean;
  /** True when the signed-in user has custom:is.admin = Yes. */
  isAdmin: boolean;
  /** False when the Cognito pool IDs are missing from .env. */
  configured: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  username: null,
  loading: true,
  isAdmin: false,
  configured: false,
  refresh: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [username, setUsername] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!authConfigured) {
      setLoading(false);
      return;
    }
    try {
      const user = await getCurrentUser();
      setUsername(user.signInDetails?.loginId || user.username);

      // Custom attributes ride along in the ID token, so this needs no extra
      // network call. Only used to show the tab — the Lambda re-checks.
      const session = await fetchAuthSession();
      setIsAdmin(session.tokens?.idToken?.payload?.['custom:is.admin'] === 'Yes');
    } catch {
      // No valid session — treated as signed out.
      setUsername(null);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    if (authConfigured) await cognitoSignOut();
    setUsername(null);
    setIsAdmin(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ username, loading, isAdmin, configured: authConfigured, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
